import { CLINIC_APIS } from "@/api_framework/api_config";
import { IClinicListResponse } from "@/api_framework/api_modals/Clinics";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { setConfOrgClinics } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { setEMROrgClinics } from "@/app_redux/reducers/slices/auth/customer_emr_slice";
import { setInventoryOrgClinics } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { setOrgClinics } from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";
import { setROTAOrgClinics } from "@/app_redux/reducers/slices/auth/rota_slice";
import { setSlotsOrgClinics } from "@/app_redux/reducers/slices/auth/slots_slice";
import { setCALENDAROrgClinics } from "@/app_redux/reducers/slices/auth/staff_calendar_slice";
import { setUIOrgClinics } from "@/app_redux/reducers/slices/ui_states_slice";
import { useConfiguration } from "@app/hooks/useConfiguration";
import axios, { AxiosError, AxiosResponse } from "axios";
import { format } from "date-fns";
import { useCallback } from "react";
import { AllowedCityClinicProviderRoutes } from "../org/useOrgCitiesListAPI";
import { ConfigurationOptionsProvidersAPI, useOrgClinicProvidersAPI } from "../org/useOrgClinicProvidersAPI";
import { useCurlLogger } from "../../useCurlLogger";

import AxiosCurlirize from "axios-curlirize";
import { setUserLoginInfo } from "@/app_redux/reducers/auth_reducer";
AxiosCurlirize(axios, () => {});

type StaffClinicListClinicOptions = {
  clinicId?: string;
  selectFirst?: boolean;
};

type ConfigurationProviderOptions = {
  cityId: string | string[];
  withClinics: boolean;
  serviceId?: string | string[];
};

export type ConfigurationOptionsClinicAPI = {
  cityIds: Array<string>;
  withProviders: boolean;
  ROTADate?: string;
  staffCategoryId?: string;
  clinicOptions?: StaffClinicListClinicOptions;
  providerOptions?: ConfigurationProviderOptions;
};

export const useStaffClinicListAPI = (forRoute: AllowedCityClinicProviderRoutes) => {
  const currentDate = format(new Date(), "yyyy-MM-dd");
  const configuration = useConfiguration();
  const dispatch = useAppDispatch();
  const { sendCurl } = useCurlLogger();
  const fetchOrgProviders = useOrgClinicProvidersAPI(forRoute);
  const userRole = useAppSelector((state) => state?.auth?.userRole);

  const execute = useCallback(
    async (configurationOptions: ConfigurationOptionsClinicAPI) => {
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        await axios
          .get(CLINIC_APIS?.CLINIC_LIST?.baseURL ?? "", {
            params: {
              city_ids: configurationOptions?.cityIds?.join(","),
            },
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          })
          .then((res: AxiosResponse<IClinicListResponse>) => {
            if (res.data.status === true) {
              dispatch(
                setUserLoginInfo({
                  data: res.data.data,
                }),
              );
              if (configurationOptions?.withProviders) {
                let clinicIds = [];
                if (configurationOptions?.clinicOptions) {
                  const { clinicOptions } = configurationOptions;

                  if (clinicOptions && clinicOptions?.selectFirst && clinicOptions?.selectFirst === true) {
                    clinicIds = [res.data.data[0]?.id];
                  }
                  if (clinicOptions && clinicOptions?.clinicId && clinicOptions?.clinicId !== "") {
                    clinicIds = [clinicOptions?.clinicId];
                  }
                } else {
                  clinicIds = res.data.data.map((c) => c.id);
                }
                let providerAPIConfig: ConfigurationOptionsProvidersAPI = {
                  ROTADate: configurationOptions?.ROTADate ?? currentDate,
                };

                if (configurationOptions?.providerOptions) {
                  providerAPIConfig = { ...providerAPIConfig, cityId: configurationOptions?.providerOptions?.cityId };
                  if (configurationOptions?.providerOptions?.withClinics) {
                    providerAPIConfig = { ...providerAPIConfig, clinicIds: clinicIds };
                  }
                  if (configurationOptions?.providerOptions?.serviceId) {
                    providerAPIConfig = {
                      ...providerAPIConfig,
                      serviceId: configurationOptions?.providerOptions?.serviceId,
                    };
                  }
                }
                fetchOrgProviders({
                  ...providerAPIConfig,
                });
                // }
              }
              switch (forRoute) {
                case "UI":
                  dispatch(
                    setUIOrgClinics({
                      data: res.data.data,
                    }),
                  );
                  break;
                case "EMR":
                  dispatch(
                    setEMROrgClinics({
                      data: res.data.data,
                    }),
                  );
                  break;
                case "INVENTORY":
                  dispatch(
                    setInventoryOrgClinics({
                      data: res.data.data,
                    }),
                  );
                  break;
                case "ROTA":
                  dispatch(
                    setROTAOrgClinics({
                      data: res.data.data,
                    }),
                  );
                  break;
                case "CONFIGURATION":
                  dispatch(
                    setConfOrgClinics({
                      data: res.data.data,
                    }),
                  );
                  break;
                case "PATIENT_WAITING_LIST":
                  dispatch(
                    setOrgClinics({
                      data: res.data.data,
                    }),
                  );
                  break;
                case "SLOTS":
                  dispatch(
                    setSlotsOrgClinics({
                      data: res.data.data,
                    }),
                  );
                  break;
                case "CALENDAR":
                  dispatch(
                    setCALENDAROrgClinics({
                      data: res.data.data,
                    }),
                  );
                  break;
                default:
                  dispatch(
                    setOrgClinics({
                      data: res.data.data,
                    }),
                  );
                  break;
              }
            } else {
              dispatch(
                setOrgClinics({
                  data: [],
                }),
              );
            }
          })
          .catch((e: AxiosError) => {
            sendCurl(e.config["curlCommand"]);
            dispatch(
              setUserLoginInfo({
                data: null,
              }),
            );
            if (e.code === "ERR_BAD_REQUEST") {
              // toast.error("Server error while fetching clinics : No Data Found");
            }
            if (e.status === 400) {
              // toast.error("Error while fetching clinics");
            }
          });
      } catch (e: any) {
        // toast.error("Server Error: " + e.message);
      }
    },
    [dispatch, userRole],
  );
  return execute;
};
